import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Refine,
  Authenticated,
  CanAccess,
  type AuthActionResponse,
  type AuthProvider,
} from '@refinedev/core';
import routerProvider, { NavigateToResource } from '@refinedev/react-router';
import { LoginNavigate } from '@admin-panel/components/lib/navigation/LoginNavigate';
import { Link, Navigate, Route, Routes, Outlet, useLocation } from 'react-router';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from '@admin-panel/store';
import { App as AntdApp, Button, ConfigProvider } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemedLayout as ThemedLayoutV2, ErrorComponent } from '@refinedev/antd';
import LKLayout from '@admin-panel/components/lib/Layout';
import { PageErrorBoundary } from '@admin-panel/components/lib/errors/PageErrorBoundary';
import { AccessControlClient } from '@admin-panel/providers/lib/access/accesscontrolprovider';
import { LKAuthProvider } from '@admin-panel/providers/lib/authProvider';
import { restDataProvider } from '@admin-panel/providers/lib/dataprovider';
import { ThemeProvider } from '@admin-panel/config/theme';
import { AccessControlConfig, getResources, PATHS_NAMES } from '@admin-panel/access-control';
import '@refinedev/antd/dist/reset.css';
import 'react-quill/dist/quill.snow.css';
import { GlobalProvider, useGlobalContext, useIsOnline } from '@admin-panel/hooks';
import { FoundationLoader } from '@admin-panel/ui';
import { lazyNamedExport } from '@admin-panel/utils';
import './global.css';
import { unlockHubAlarmAudio } from './hubAlerts/hubAlarm';
import { markBriefAfterLogin } from './hubAlerts/hubBriefingSession';
import { hubLoginFailure, hubLoginRefused } from './hubLogin';
import {
  HubAlertSetupGate,
  HubAlertSetupProvider,
  SETUP_PATH,
} from './hubAlertSetup/HubAlertSetupGate';
import HubHeaderTools from './headerTools/HubHeaderTools';
import SetupReporter from './hubAlertSetup/SetupReporter';
import AlertCenter from './hubAlerts/alertCenter/AlertCenter';
import { AlertCenterBoundary } from './hubAlerts/alertCenter/AlertCenterBoundary';
import { discardInbox } from './hubAlerts/alertCenter/alertCenterStore';
import { resetRegistrationStatus } from './hubAlertSetup/hubAlertSetupState';
import ReleaseWatcher from './releaseWatcher/ReleaseWatcher';
import ChooseFacility from './ChooseFacility';
import AppLinkHandler from './appLinks/AppLinkHandler';
import { FacilityScope } from './FacilityScope';
import { retiredHubRoutes } from './retiredRoutes';
import { useHubNotificationProvider } from './hubNotifications';
import { HubFacilityGate } from './HubFacilityGate';

const TodayWorklist = lazyNamedExport(
  () => import('@admin-panel/pages/orders/today'),
  'TodayWorklist',
);
/** Touch screens (no hover, by capability, not device): buttons keep their resting colours when tapped. */
const NO_HOVER = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none)').matches;
const STILL_BUTTONS = {
  colorPrimaryHover: '#393AFB',
  defaultHoverBg: '#ffffff',
  defaultHoverColor: '#171717',
  defaultHoverBorderColor: '#d9d9d9',
  colorLinkHover: '#393AFB',
  textHoverBg: 'transparent',
  colorErrorHover: '#ff4d4f',
  colorErrorBorderHover: '#ffa39e',
};

const loadHubAlerts = () => import('./hubAlerts/HubAlerts');

/**
 * Order alerts load on their own. Their file not loading (no signal right after login) must never take Hub down: the
 * page went blank with only a "Nothing saved" toast (round 20, Deepa chose a facility offline). They render nothing and
 * load again once the signal is back (a failed lazy import stays failed, so each try is a new one).
 */
const HubAlertsWhenLoaded = () => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const Alerts = useMemo(() => lazy(loadHubAlerts), [attempt]);
  useEffect(() => {
    if (!failed) return;
    const online = () => {
      setFailed(false);
      setAttempt(a => a + 1);
    };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [failed]);
  if (failed) return null;
  return (
    <AlertCenterBoundary key={attempt} onError={() => setFailed(true)}>
      <Suspense fallback={null}>
        <Alerts />
      </Suspense>
    </AlertCenterBoundary>
  );
};
const HubAlertSetupPage = lazy(() => import('./hubAlertSetup/HubAlertSetupPage'));
const Login = lazyNamedExport<{ title?: string }>(
  () => import('@admin-panel/pages/auth/login'),
  'Login',
);
const WarehouseForgotPassword = lazy(
  () => import('@admin-panel/pages/auth/WarehouseForgotPassword'),
);
const WarehouseResetPassword = lazy(() => import('@admin-panel/pages/auth/WarehouseResetPassword'));
const HubHome = lazyNamedExport(() => import('@admin-panel/pages/dashboard'), 'HubHome');
const ListVendors = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'ListVendors');
const VendorShow = lazyNamedExport(() => import('@admin-panel/pages/vendors'), 'VendorShow');
const SkuEnquiryHistory = lazyNamedExport(
  () => import('@admin-panel/pages/vendors'),
  'SkuEnquiryHistory',
);
const ListSkus = lazyNamedExport(() => import('@admin-panel/pages/skus'), 'ListSkus');
const ListWarehouseInventory = lazyNamedExport(
  () => import('@admin-panel/pages/inventory'),
  'ListWarehouseInventory',
);
const BetterInventory = lazyNamedExport<{
  headerButtons?: ReactNode;
  showInventoryControls?: boolean;
  showProductInventory?: boolean;
  showBarcodeScanner?: boolean;
}>(() => import('@admin-panel/pages/BetterInventory'), 'BetterInventory');
const StoreImages = lazyNamedExport<{ showShelfwiseImageQueue?: boolean }>(
  () => import('@admin-panel/pages/StoreImages'),
  'StoreImages',
);
const InwardGrnList = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'InwardGrnList');
const ReceivePage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ReceivePage');
const ScanInPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ScanInPage');
const ActivityPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ActivityPage');
// Stage only: the rider-QR flow (parcels and cash), reached from Rider handover's "Scan rider QR".
const InwardRiderQrHandover = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'RiderHandoverPage',
);
const B2CShipmentsPage = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'B2CShipmentsPage',
);
const IncomingShipmentsPage = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'IncomingShipmentsPage',
);
const InwardGrnScan = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'InwardGrnScan');
const InwardPutawayList = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'InwardPutawayList',
);
const InwardPutawayScan = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'InwardPutawayScan',
);
const ShelvesPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ShelvesPage');
const OrderPickingList = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'OrderPickingList',
);
const OrderPicklistScan = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'OrderPicklistScan',
);
const PackScanPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'PackScanPage');
const ScmRepairsPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ScmRepairsPage');
const StockCountPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'StockCountPage');
const StockAdjustmentPage = lazyNamedExport(
  () => import('@admin-panel/pages/inward'),
  'StockAdjustmentPage',
);
const ItemsPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ItemsPage');
const ShelfStockPage = lazyNamedExport(() => import('@admin-panel/pages/inward'), 'ShelfStockPage');
const ShelfTransferPage = lazyNamedExport(
  () => import('@admin-panel/pages/shelf-transfer'),
  'ShelfTransferPage',
);
const PutBackPage = lazyNamedExport(() => import('@admin-panel/pages/put-back'), 'PutBackPage');
const RepairsPage = lazyNamedExport(() => import('@admin-panel/pages/repairs'), 'RepairsPage');
const ListOrderPackagesForWH = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ListOrderPackagesForWH',
);
const OrderControlCenter = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'OrderControlCenter',
);
const ShowOrderPackage = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowOrderPackage',
);
const EditDeliveryChallan = lazyNamedExport(
  () => import('@admin-panel/pages/dc'),
  'EditDeliveryChallan',
);
const ShowDeliveryChallan = lazyNamedExport<{ view?: string }>(
  () => import('@admin-panel/pages/dc'),
  'ShowDeliveryChallan',
);
const GRNList = lazyNamedExport(() => import('@admin-panel/pages/grn'), 'GRNList');
const ListMTOPurchaseOrders = lazyNamedExport(
  () => import('@admin-panel/pages/po'),
  'ListMTOPurchaseOrders',
);
const IncomingStockTransfersWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'IncomingStockTransfersWrapper',
);
const OutgoingStockTransfersWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'OutgoingStockTransfersWrapper',
);
const EditStockTransfer = lazyNamedExport(
  () => import('@admin-panel/pages/stocktransfer'),
  'EditStockTransfer',
);
const GatepassOrderShow = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'GatepassOrderShow',
);
const GatepassScan = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'GatepassScan');
const BulkPick = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'BulkPick');
const BulkPickScan = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'BulkPickScan');
const CreatePicklists = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'CreatePicklists',
);
const Picklists = lazyNamedExport(() => import('@admin-panel/pages/picklist'), 'Picklists');
const OnDemandPick = lazyNamedExport(() => import('@admin-panel/pages/picklist'), 'OnDemandPick');
const OnDemandPickScan = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'OnDemandPickScan',
);
const PicklistGatepasses = lazyNamedExport(
  () => import('@admin-panel/pages/picklist'),
  'PicklistGatepasses',
);
const IncomingManifestsWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'IncomingManifestsWrapper',
);
const OutgoingManifestsWrapper = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'OutgoingManifestsWrapper',
);
const CreateManifest = lazyNamedExport(
  () => import('@admin-panel/pages/gatepass'),
  'CreateManifest',
);
const ShowManifest = lazyNamedExport(() => import('@admin-panel/pages/gatepass'), 'ShowManifest');
const CancelledAwbMovementExceptions = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'CancelledAwbMovementExceptions',
);
const ListReturnRequests = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ListReturnRequests',
);
const ShowReturnRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/return'),
  'ShowReturnRequest',
);
const ShowOrderLine = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/show'),
  'ShowOrderLine',
);
const ShowCustomerOrder = lazyNamedExport(
  () => import('@admin-panel/pages/orders'),
  'ShowCustomerOrder',
);
const CashHandover = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'CashHandover');
const HubFacilityHandover = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'HubFacilityHandover',
);
const RiderHandoverPage = lazyNamedExport<{ actions?: ReactNode }>(
  () => import('@admin-panel/pages/Logistics'),
  'RiderHandoverPage',
);
const ReceiveLinkPage = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'ReceiveLinkPage',
);
const DeliveryScanSupport = lazyNamedExport(
  () => import('@admin-panel/pages/orders/packages/DeliveryScanSupport'),
  'DeliveryScanSupport',
);
const RiderAttendanceQr = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'RiderAttendanceQr',
);
const MyRides = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'MyRides');
const MyRideDetails = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'MyRideDetails',
);
const RiderHealth = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'RiderHealth');
const RiderDay = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'RiderDay');
const RiderManagement = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'RiderManagement',
);
const OpenWorkMap = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'OpenWorkMap');
const ReturnRideDetails = lazyNamedExport(
  () => import('@admin-panel/pages/Logistics'),
  'ReturnRideDetails',
);
const DashOrders = lazy(() => import('@admin-panel/pages/orders/DashOrders'));
const Logistics = lazyNamedExport(() => import('@admin-panel/pages/Logistics'), 'Logistics');
const RefundsOnhold = lazyNamedExport(
  () => import('@admin-panel/pages/orders/RefundsOnhold'),
  'RefundsOnhold',
);
const ShowRefundRequest = lazyNamedExport(
  () => import('@admin-panel/pages/orders/items/refund'),
  'ShowRefundRequest',
);
const ShowFacility = lazyNamedExport(() => import('@admin-panel/pages/facility'), 'ShowFacility');
const CycleCount = lazyNamedExport(() => import('@admin-panel/pages/cycle-count'), 'CycleCount');
const ShowB2BCustomer = lazyNamedExport(
  () => import('@admin-panel/pages/users'),
  'ShowB2BCustomer',
);
const PageNotFound = lazyNamedExport<{ reason?: 'unauthorized' | 'not_found' }>(
  () => import('@admin-panel/pages/PageNotFound'),
  'PageNotFound',
);

// Login allows the alarm sound and asks for the start-of-day briefing; logout retires this
// device's alert token so it stops ringing for the previous user.
const hubAuthProvider: AuthProvider = {
  ...LKAuthProvider,
  login: async params => {
    // The login tap is a user gesture: use it to allow the alarm sound (also on iPhone).
    void unlockHubAlarmAudio();
    let result: AuthActionResponse;
    try {
      result = await LKAuthProvider.login(params);
    } catch (error) {
      // Plain words, never "AxiosError" (round 30: sign-in service restarting).
      return hubLoginFailure(error);
    }
    if (result.success) {
      // The first screen after login shows what is waiting, even if this device briefed today.
      markBriefAfterLogin();
    }
    return hubLoginRefused(result);
  },
  logout: async params => {
    resetRegistrationStatus();
    // Loaded lazily so Firebase stays out of the main bundle.
    await import('./hubAlerts/hubPushRegistration')
      .then(({ closeHubNotifications, retireHubAlertTokenOnLogout }) =>
        Promise.all([closeHubNotifications(), retireHubAlertTokenOnLogout()]),
      )
      .catch(() => undefined);
    return LKAuthProvider.logout(params);
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

function Application() {
  useIsOnline();
  // The home page's file is fetched while there is a signal (the login page), so choosing a facility with none still
  // opens the tiles, each marked "did not load", never "This page didn't load" (round 20, Deepa).
  useEffect(() => {
    void import('@admin-panel/pages/dashboard').catch(() => undefined);
  }, []);

  const resources = useMemo(() => getResources('WAREHOUSE'), []);
  const accessControlClient = useMemo(() => new AccessControlClient(), []);

  return (
    <ReduxProvider store={store}>
      <ThemeProvider
        theme={{
          token: { colorPrimary: '#393AFB', colorBgLayout: '#ffffff' },
        }}
      >
        <ConfigProvider
          // No ripple on every tap: decoration on the most-tapped surface. Press feedback is :active (app.css).
          wave={{ disabled: true }}
          theme={{
            token: {
              colorPrimary: '#393AFB',
              colorBgLayout: '#ffffff',
              fontFamily: '"Hanken Grotesk"',
              colorText: '#171717',
            },
            // A finger has no hover: a tapped button would keep its hover colour until the next tap.
            components: NO_HOVER ? { Button: STILL_BUTTONS } : undefined,
          }}
        >
          <AntdApp>
            <ReleaseWatcher />
            <AppLinkHandler />
            <QueryClientProvider client={queryClient}>
              <Refine
                authProvider={hubAuthProvider}
                accessControlProvider={accessControlClient.getAccessControlProvider()}
                dataProvider={restDataProvider}
                notificationProvider={useHubNotificationProvider}
                routerProvider={routerProvider}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: 'Unny6d-jT5kpU-QOXAra',
                  // Refine's own query client: a write is sent now or fails now, never held offline and sent by
                  // itself when the signal returns. Held, it applied with no tap while the screen said "No network.
                  // Nothing saved." (round 29, Sunita: Confirm packing for 3045 spun offline and packed the parcel
                  // 9 s after the signal came back). Reads keep Refine's defaults.
                  reactQuery: { clientConfig: { defaultOptions: { mutations: { networkMode: 'always' } } } },
                }}
                resources={resources}
              >
                <AccessControlConfig resources={resources} dataprovider={accessControlClient}>
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-routes"
                          fallback={<LoginNavigate to="/login" />}
                        >
                          <GlobalProvider>
                            {/* <LkNotificationsProvider> */}
                            <HubAlertSetupProvider>
                              <SetupReporter />
                              <AlertCenterBoundary onError={discardInbox}>
                                <AlertCenter />
                              </AlertCenterBoundary>
                              <LKLayout headerTools={<HubHeaderTools />}>
                                <MainOutlet />
                              </LKLayout>
                            </HubAlertSetupProvider>
                            {/* </LkNotificationsProvider> */}
                          </GlobalProvider>
                        </Authenticated>
                      }
                    >
                      <Route index path="/" element={<HubHome />} />
                      <Route path={SETUP_PATH} element={<HubAlertSetupPage />} />
                      <Route path="/packages">
                        <Route index path="" element={<ListOrderPackagesForWH />} />
                        <Route path="show/:id" element={<ShowOrderPackage />} />
                      </Route>
                      <Route index path="/ordercontrolcenter" element={<OrderControlCenter />} />
                      <Route
                        path="dash-orders"
                        element={
                          <CanAccess
                            resource={PATHS_NAMES.DASH_ORDERS}
                            action="list"
                            fallback={<PageNotFound reason="unauthorized" />}
                          >
                            <DashOrders />
                          </CanAccess>
                        }
                      />
                      <Route path="refunds-onhold" element={<RefundsOnhold />} />
                      <Route path="returns">
                        <Route path="" index element={<ListReturnRequests />} />
                        <Route
                          path="exceptions/cancelled-awb-movement"
                          element={<CancelledAwbMovementExceptions />}
                        />
                        <Route path="show/:id" index element={<ShowReturnRequest />} />
                      </Route>
                      <Route path="refunds">
                        <Route path="show/:id" element={<ShowRefundRequest />} />
                      </Route>
                      <Route path="/inventory">
                        <Route index path="" element={<ListWarehouseInventory />} />
                      </Route>
                      <Route index path="/cycle-count" element={<CycleCount />} />
                      <Route element={<Outlet />}>
                        <Route index element={null} />

                        <Route path="/vendors">
                          <Route index path="" element={<ListVendors />} />
                          <Route path="show/:id" element={<VendorShow />} />
                          <Route path="sku-enquiry-history" element={<SkuEnquiryHistory />} />
                        </Route>
                        <Route path="/skus">
                          <Route index path="" element={<ListSkus />} />
                        </Route>
                      </Route>

                      <Route element={<Outlet />}>
                        <Route path="/grn">
                          <Route index path="" element={<GRNList />} />
                          <Route
                            index
                            path="show/:id"
                            element={<ShowDeliveryChallan view="warehouse" />}
                          />
                          <Route index path="edit/:id" element={<EditDeliveryChallan />} />
                        </Route>
                        <Route path="/inward">
                          <Route
                            path="receive"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_RECEIVE}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <ReceivePage />
                              </CanAccess>
                            }
                          />
                          {/* Stage wrapper (4 Nov): the release's list owns the route; Apoorv's QR flow is one tap away. */}
                          <Route
                            path="rider-handover"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.RIDER_HANDOVER}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <RiderHandoverPage
                                  actions={
                                    <Link to="/inward/rider-handover/rider-qr">
                                      <Button size="large" style={{ minHeight: 48 }}>
                                        Scan rider QR
                                      </Button>
                                    </Link>
                                  }
                                />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="rider-handover/rider-qr"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.RIDER_HANDOVER}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <InwardRiderQrHandover />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="activity"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_ACTIVITY}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <ActivityPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="incoming"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_INCOMING_SHIPMENTS}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <IncomingShipmentsPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="receive/:id"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_RECEIVE}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <ScanInPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="grn"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_GRN}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <InwardGrnList />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="grn/:id"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_GRN}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <InwardGrnScan />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="putaway"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.PUTAWAY}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <InwardPutawayList />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="putaway/:id"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.PUTAWAY}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <InwardPutawayScan />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="picking"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.ORDER_PICKING}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <OrderPickingList />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="picking/:id"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.ORDER_PICKING}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <OrderPicklistScan />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="pack"
                            element={
                              <CanAccess
                                resource="packages"
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <PackScanPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="repairs"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_REPAIRS}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <ScmRepairsPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="stock-count"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_STOCK_COUNT}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <StockCountPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="stock-adjustment"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_STOCK_ADJUSTMENT}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <StockAdjustmentPage />
                              </CanAccess>
                            }
                          />
                          <Route
                            path="items"
                            element={
                              <CanAccess
                                resource={PATHS_NAMES.SCM_ITEMS}
                                action="list"
                                fallback={<PageNotFound reason="unauthorized" />}
                              >
                                <ItemsPage />
                              </CanAccess>
                            }
                          />
                        </Route>
                        <Route path="mtopurchaseorders">
                          <Route index path="" element={<ListMTOPurchaseOrders />} />
                        </Route>
                        <Route path="incoming-stock-transfers">
                          <Route index path="" element={<IncomingStockTransfersWrapper />} />
                        </Route>
                        <Route path="outgoing-stock-transfers">
                          <Route index path="" element={<OutgoingStockTransfersWrapper />} />
                        </Route>
                        <Route path="stocktransfer">
                          <Route path="edit/:id" element={<EditStockTransfer />} />
                        </Route>
                        <Route path="incoming-manifests">
                          <Route index path="" element={<IncomingManifestsWrapper />} />
                        </Route>
                        <Route path="outgoing-manifests">
                          <Route index path="" element={<OutgoingManifestsWrapper />} />
                        </Route>
                        <Route path="outgoing-courier">
                          <Route index path="" element={<B2CShipmentsPage />} />
                        </Route>
                        <Route path="manifests">
                          <Route path="create" element={<CreateManifest />} />
                          <Route path=":id/show" element={<ShowManifest />} />
                        </Route>
                        <Route path="gatepass-orders">
                          <Route path="show/:id" element={<GatepassOrderShow />} />
                        </Route>
                        <Route path="gatepasses">
                          <Route path=":gatepassId/scan" element={<GatepassScan />} />
                        </Route>
                        <Route path="bulk-pick">
                          <Route index element={<BulkPick />} />
                          <Route path="pick" element={<BulkPickScan />} />
                        </Route>
                        <Route path="picklists">
                          <Route index element={<Picklists />} />
                          <Route path="create" element={<CreatePicklists />} />
                          <Route path="gatepasses" element={<PicklistGatepasses />} />
                        </Route>
                        <Route path="on-demand-pick">
                          <Route index element={<OnDemandPick />} />
                          <Route path=":stockTransferId" element={<OnDemandPickScan />} />
                        </Route>
                        <Route path="lineitems/show/:id" element={<ShowOrderLine />} />
                        <Route path="orders/show/:id" element={<ShowCustomerOrder />} />
                        {/* The rider's handover QR: /receive#w=TYPE:id&c=code (kept through login). */}
                        <Route
                          path="receive"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.FACILITY_HANDOVER}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <ReceiveLinkPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="today"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.TODAY}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <TodayWorklist />
                            </CanAccess>
                          }
                        />
                        {retiredHubRoutes()}
                        <Route path="facilities/show/:id" element={<ShowFacility />} />
                        <Route path="b2b-customer/show/:id" element={<ShowB2BCustomer />} />
                      </Route>

                      <Route path="/utility" element={<Outlet />}>
                        <Route
                          path="product-image-search"
                          element={
                            <BetterInventory
                              headerButtons={null}
                              showInventoryControls={false}
                              showProductInventory
                              showBarcodeScanner
                            />
                          }
                        />
                        <Route path="store-images">
                          <Route index path="" element={<StoreImages showShelfwiseImageQueue />} />
                        </Route>
                        <Route
                          path="shelves"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.SHELVES}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <ShelvesPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="shelf-stock"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.SHELF_STOCK}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <ShelfStockPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="shelf-transfer"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.SHELF_TRANSFER}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <ShelfTransferPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="put-back"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.PUT_BACK}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <PutBackPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="repairs"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.REPAIRS}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RepairsPage />
                            </CanAccess>
                          }
                        />
                      </Route>
                      <Route path="/logistics" element={<Logistics />}>
                        {/* /logistics was a blank page: land managers on the Rider Control Centre, riders on My Rides. */}
                        <Route
                          index
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_HEALTH}
                              action="list"
                              fallback={<Navigate to="/logistics/my-rides" replace />}
                            >
                              <Navigate to="/logistics/rider-health" replace />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="open-work-map"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.OPEN_WORK_MAP}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <OpenWorkMap />
                            </CanAccess>
                          }
                        />
                        <Route path="my-rides/:packageId" element={<MyRideDetails />} />
                        <Route path="my-rides/return/:returnId" element={<ReturnRideDetails />} />
                        <Route path="cash-handover" element={<CashHandover />} />
                        <Route
                          path="facility-handover"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.FACILITY_HANDOVER}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <HubFacilityHandover />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="delivery-scan-support"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.DELIVERY_SCAN_SUPPORT}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <DeliveryScanSupport />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="attendance-qr"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_ATTENDANCE_QR}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderAttendanceQr />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="riders"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_MANAGEMENT}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderManagement />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="rider-health"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_HEALTH}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderHealth />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="rider-health/:riderId/day"
                          element={
                            <CanAccess
                              resource={PATHS_NAMES.RIDER_HEALTH}
                              action="list"
                              fallback={<PageNotFound reason="unauthorized" />}
                            >
                              <RiderDay />
                            </CanAccess>
                          }
                        />
                        <Route index path="my-rides" element={<MyRides />} />
                      </Route>
                    </Route>
                    <Route
                      path="/forgot-password"
                      element={
                        <Suspense fallback={<FoundationLoader />}>
                          <WarehouseForgotPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/reset-password"
                      element={
                        <Suspense fallback={<FoundationLoader />}>
                          <WarehouseResetPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      element={
                        <Authenticated key="auth-pages" fallback={<Outlet />}>
                          <NavigateToResource resource="dashboard" />
                        </Authenticated>
                      }
                    >
                      <Route
                        path="/login"
                        element={
                          <Suspense fallback={<FoundationLoader />}>
                            <Login title="Login to Hub" />
                          </Suspense>
                        }
                      />
                    </Route>

                    <Route
                      element={
                        <Authenticated key="catch-all">
                          <ThemedLayoutV2>
                            <Outlet />
                          </ThemedLayoutV2>
                        </Authenticated>
                      }
                    >
                      <Route path="*" element={<ErrorComponent />} />
                    </Route>
                  </Routes>
                </AccessControlConfig>
              </Refine>
            </QueryClientProvider>
          </AntdApp>
        </ConfigProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
}

const MainOutlet = () => {
  const { warehouseId, isAllFacilities } = useGlobalContext();
  const { pathname } = useLocation();
  // Alert setup works before a facility is chosen.
  const facilityReady = Boolean(warehouseId || isAllFacilities) || pathname === SETUP_PATH;

  return (
    <>
      <HubAlertsWhenLoaded />
      <HubAlertSetupGate>
        <HubFacilityGate facilityReady={facilityReady} fallback={<ChooseFacility />}>
          <PageErrorBoundary
            resetKey={pathname}
            subTitle="Check the internet connection, then reload. New order alerts keep working."
          >
            <FacilityScope>
              <Suspense fallback={<FoundationLoader />}>
                <Outlet />
              </Suspense>
            </FacilityScope>
          </PageErrorBoundary>
        </HubFacilityGate>
      </HubAlertSetupGate>
    </>
  );
};

export default Application;
